import React, { useEffect, useState } from "react";

/**
 * A component that displays a user's name.
 *
 * @param {object} ScriptProps The component props.
 * @param {React.RefObject<HTMLElement | null>} ScriptProps.targetElementRef React Ref of the element the embedded html will be put once retrieved.
 * @param {string} [ScriptProps.src] The src used for the Script.
 * @param {string} [ScriptProps.scriptId] The id used for the Script.
 * @param {boolean} [ScriptProps.async] The async prop used for the Script (defaults to true).
 */
interface ScriptProps {
  targetElementRef: React.RefObject<HTMLElement | null>;
  src?: string;
  scriptId?: string;
  async?: boolean;
}

function useEmbeddedHtml({
  targetElementRef,
  src,
  scriptId,
  async
}: ScriptProps) {
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if(!src || !targetElementRef) return;

    setIsLoading(true);
    if (!targetElementRef.current) {
      setIsLoading(false);
      return;
    }

    const currentRef = targetElementRef.current;

    const script = document.createElement("script");
    script.async = async ?? true;
    script.src = src ?? "";
    script.id = scriptId ?? "";

    targetElementRef?.current.appendChild(script);
    setIsLoading(false);

    // eslint-disable-next-line consistent-return
    return () => {
      currentRef.removeChild(script);
    };

  }, [async, scriptId, src, targetElementRef]);

  return { isLoading };
}

export default useEmbeddedHtml;
